import { defineStore } from 'pinia'
import servicesApi from '@/api/services'
import customersApi from '@/api/customers'
import routesApi from '@/api/routes'
import invoicesApi from '@/api/invoices'

export const useOnboardingStore = defineStore('onboarding', {
  state: () => ({
    dismissed: false,
    steps: {
      service: false,
      customer: false,
      route: false,
      invoice: false,
    },
    loaded: false,
    collapsed: false,
  }),

  getters: {
    allDone: (state) => state.steps.service && state.steps.customer && state.steps.route && state.steps.invoice,
    visible: (state) =>
      state.loaded && !state.dismissed && !(state.steps.service && state.steps.customer && state.steps.route && state.steps.invoice),
    completedCount: (state) =>
      [state.steps.service, state.steps.customer, state.steps.route, state.steps.invoice].filter(Boolean).length,
  },

  actions: {
    _key(userId) {
      return `mowapp_onboarding_${userId}`
    },

    load(userId) {
      if (!userId) return
      const raw = localStorage.getItem(this._key(userId))
      if (raw) {
        try {
          const saved = JSON.parse(raw)
          this.dismissed = saved.dismissed ?? false
          this.collapsed = saved.collapsed ?? false
          this.steps = { service: false, customer: false, route: false, invoice: false, ...saved.steps }
        } catch {
          // ignore malformed data
        }
      }
      this.loaded = true
    },

    _save(userId) {
      if (!userId) return
      localStorage.setItem(
        this._key(userId),
        JSON.stringify({ dismissed: this.dismissed, collapsed: this.collapsed, steps: this.steps }),
      )
    },

    dismiss(userId) {
      this.dismissed = true
      this._save(userId)
    },

    toggleCollapsed(userId) {
      this.collapsed = !this.collapsed
      this._save(userId)
    },

    markStep(step, userId) {
      if (this.steps[step]) return
      this.steps[step] = true
      this._save(userId)
    },

    async checkProgress(userId) {
      if (this.dismissed) return
      if (this.steps.service && this.steps.customer && this.steps.route && this.steps.invoice) return
      try {
        const [s, c, r, i] = await Promise.all([
          servicesApi.getAll(),
          customersApi.getAll(),
          routesApi.getAll(),
          invoicesApi.getAll(),
        ])
        if (s.data?.services?.length > 0) this.steps.service = true
        if (c.data?.customers?.length > 0) this.steps.customer = true
        if (r.data?.routes?.length > 0) this.steps.route = true
        if (i.data?.invoices?.length > 0) this.steps.invoice = true
        this._save(userId)
      } catch {
        // silent — onboarding is non-critical
      }
    },

    reset() {
      this.dismissed = false
      this.collapsed = false
      this.steps = { service: false, customer: false, route: false, invoice: false }
      this.loaded = false
    },
  },
})
